import { ColumnDef } from "@tanstack/react-table";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Download, ExternalLink, FileText, LockKeyhole } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import CustomTable from "components/shared/table";
import { SettingsPageLayout } from "components/settings/settings-page-layout";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import { PermissionsWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data/queries-key";
import { CustomerInvoice } from "data/models";
import { fetchInvoices } from "data/queries-common";
import { formatPrice } from "utils/format";
import { formatTimestamp } from "utils/format-date";
import { resolveIdentityId } from "utils/identity";

const statusLabels: Record<CustomerInvoice["status"], string> = {
  paid: "Paid",
  open: "Unpaid",
  void: "Void",
  uncollectible: "Uncollectible",
};

const statusClasses: Record<CustomerInvoice["status"], string> = {
  paid: "border-success/30 bg-success/10 text-success-foreground",
  open: "border-warning/30 bg-warning/10 text-warning-foreground",
  void: "border-border bg-muted text-muted-foreground",
  uncollectible: "border-destructive/30 bg-destructive/10 text-destructive",
};

const invoiceColumns: ColumnDef<CustomerInvoice>[] = [
  {
    id: "invoice",
    header: "Invoice",
    enableSorting: false,
    cell: ({ row }) => (
      <span className="font-medium">{row.original.number ?? row.original.id}</span>
    ),
  },
  {
    accessorKey: "created",
    header: "Date",
    enableSorting: false,
    cell: ({ row }) => formatTimestamp(row.original.created * 1000),
  },
  {
    accessorKey: "description",
    header: "Description",
    enableSorting: false,
    cell: ({ row }) => (
      <span title={row.original.description || undefined}>
        {row.original.description || "—"}
      </span>
    ),
  },
  {
    accessorKey: "total",
    header: () => <div className="text-right">Total</div>,
    enableSorting: false,
    cell: ({ row }) => (
      <div className="text-right">
        {formatPrice(row.original.total / 100)}
      </div>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    enableSorting: false,
    cell: ({ row }) => (
      <Badge variant="outline" className={statusClasses[row.original.status]}>
        {statusLabels[row.original.status]}
      </Badge>
    ),
  },
  {
    id: "documents",
    header: () => <div className="text-right">Documents</div>,
    enableSorting: false,
    cell: ({ row }) => {
      const invoice = row.original;
      return (
        <div className="text-right">
          <div className="inline-flex gap-2 whitespace-nowrap">
            {invoice.receiptUrl && (
              <Button variant="outline" size="sm" asChild>
                <a
                  href={invoice.receiptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`View receipt for invoice ${invoice.number ?? invoice.id}`}
                >
                  <ExternalLink /> Receipt
                </a>
              </Button>
            )}
            {invoice.invoicePdf ? (
              <Button variant="outline" size="sm" asChild>
                <a
                  href={invoice.invoicePdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Download invoice ${invoice.number ?? invoice.id}`}
                >
                  <Download /> Invoice
                </a>
              </Button>
            ) : invoice.invoiceUrl ? (
              <Button variant="outline" size="sm" asChild>
                <a
                  href={invoice.invoiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`View invoice ${invoice.number ?? invoice.id}`}
                >
                  <ExternalLink /> Invoice
                </a>
              </Button>
            ) : null}
            {!invoice.invoicePdf && !invoice.invoiceUrl && !invoice.receiptUrl && (
              <span className="text-muted-foreground">Unavailable</span>
            )}
          </div>
        </div>
      );
    },
  },
];

export function InvoicesPage() {
  const [identity] = useIdentity();
  const identityId = resolveIdentityId(identity);
  const { isPermitted, permissionsQuery } = usePermissions();
  const invoicesQuery = useInfiniteQuery({
    queryKey: [QueryKeys.invoices, identityId],
    queryFn: ({ pageParam, signal }) => fetchInvoices(identityId, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: Boolean(identityId) && isPermitted([PermissionsKeys.invoicesRead]),
  });
  const invoices = invoicesQuery.data?.pages.flatMap((page) => page.invoices) ?? [];

  return (
    <SettingsPageLayout
      pageTitle={{
        icon: FileText,
        title: "Invoices & receipts",
        description: "Only Stripe invoices and receipts are shown here. For MYOB invoices, contact support.",
      }}
    >
      <div className="mx-auto max-w-5xl space-y-4">
        {permissionsQuery.isPending ? (
          <Skeleton className="h-48 w-full" aria-label="Checking invoice access" />
        ) : permissionsQuery.isError ? (
          <div className="space-y-4">
            <ReactQueryErrorWrapper queries={[permissionsQuery]} />
            <Button
              variant="outline"
              onClick={() => permissionsQuery.refetch()}
              disabled={permissionsQuery.isFetching}
            >
              Retry
            </Button>
          </div>
        ) : (
          <PermissionsWrapper
            keys={[PermissionsKeys.invoicesRead]}
            messages={{
              [PermissionsReasons.ROLE_DENY]: (
                <Card className="mx-auto w-full max-w-xl" role="status">
                  <CardContent className="flex items-start gap-4 px-6 py-2">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <LockKeyhole className="size-5" aria-hidden="true" />
                    </div>
                    <div className="space-y-2">
                      <h2 className="text-base font-semibold">Invoice access is restricted</h2>
                      <p className="text-sm text-muted-foreground">
                        Invoices and receipts are available to organisation owners, admins and billing members.
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Ask your organisation owner or admin to update your role if you need access.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ),
            }}
          >
            <ReactQueryErrorWrapper queries={[invoicesQuery]} />
            {invoicesQuery.isPending && (
              <Skeleton className="h-48 w-full" aria-label="Loading invoices" />
            )}
            {invoicesQuery.isSuccess && invoices.length === 0 && (
              <div className="py-10 text-center" role="status">
                <FileText className="mx-auto mb-3 size-8 text-muted-foreground" />
                <p className="font-medium">No Stripe invoices found</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {invoicesQuery.hasNextPage
                    ? "There are no customer invoices in the loaded history. Load older invoices to continue."
                    : "Your invoices will appear here when you are billed through Stripe."}
                </p>
              </div>
            )}
            {invoices.length > 0 && (
              <div role="region" aria-label="Stripe invoice history">
                <CustomTable
                  className="rounded-md bg-card"
                  rows={invoices}
                  columns={invoiceColumns}
                  isLoading={false}
                  overlayText="No Stripe invoices found"
                  getRowId={(invoice) => invoice.id}
                  pagination={false}
                />
              </div>
            )}
            {invoicesQuery.isError && (
              <Button
                variant="outline"
                onClick={() => invoicesQuery.refetch()}
                disabled={invoicesQuery.isFetching}
              >
                Retry
              </Button>
            )}
            {invoicesQuery.hasNextPage && (
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => invoicesQuery.fetchNextPage()}
                  disabled={invoicesQuery.isFetching}
                >
                  {invoicesQuery.isFetchingNextPage ? "Loading…" : "Load older invoices"}
                </Button>
              </div>
            )}
          </PermissionsWrapper>
        )}
      </div>
    </SettingsPageLayout>
  );
}
